import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, Sparkles, FileText, Loader2, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import { http, pdfUrl } from "@/lib/api";

const PRESETS = { static: [0, 3, 5, 10, 20], ai: [3, 7, 12, 20] };

const TypeCard = ({ active, onClick, icon: Icon, title, sub, testId }) => (
  <button data-testid={testId} onClick={onClick}
    className={`text-left rounded-xl border p-4 transition-colors ${active ? "border-[#D96B43] bg-[#D96B43]/8 ring-2 ring-[#D96B43]/25" : "border-[#1C242B]/12 hover:border-[#1C242B]/40"}`}>
    <Icon size={18} className="text-[#D96B43]" />
    <p className="font-display font-semibold mt-2">{title}</p>
    <p className="text-xs text-slate-500 mt-1">{sub}</p>
  </button>
);

export const DownloadModal = ({ open, onOpenChange, token, pricing, aiPaid, aiReady, defaultType = "static" }) => {
  const [type, setType] = useState(defaultType);
  const [amount, setAmount] = useState(defaultType === "ai" ? 7 : 5);
  const [busy, setBusy] = useState(false);
  const tier = pricing[type];

  const pick = (t) => { setType(t); setAmount(pricing[t].suggested); };

  const go = async () => {
    if (amount === "" || isNaN(Number(amount))) { toast.error("Please enter an amount (0 is fine for the static report)"); return; }
    if (type === "static" && Number(amount) === 0) {
      window.location.href = pdfUrl(token, "static");
      toast.success("Your free report is downloading. Thank you for using the tool.");
      onOpenChange(false);
      return;
    }
    if (type === "ai" && aiPaid) {
      if (aiReady) window.location.href = pdfUrl(token, "ai");
      else toast.message("Your AI analysis is still being written; check the report page in a moment.");
      return;
    }
    if (Number(amount) < tier.min) { toast.error(`Minimum for this option is $${tier.min}`); return; }
    setBusy(true);
    try {
      const { data } = await http.post("/payments/checkout", { token, report_type: type, amount: Number(amount), origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const cta = amount === "" ? "Enter an amount" : type === "static" && Number(amount) === 0 ? "Download free PDF" : type === "ai" && aiPaid ? "Download AI report" : `Contribute $${Number(amount || 0).toFixed(2)} & continue`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-[#FAF8F5]" data-testid="download-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Download your report</DialogTitle>
          <DialogDescription>This tool is free. Pay what you want if it helped; $0 is completely okay for the static report.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <TypeCard testId="modal-type-static" active={type === "static"} onClick={() => pick("static")} icon={FileText} title="Static evaluation" sub="Rule-based scoring · $0 allowed · $5 suggested" />
          <TypeCard testId="modal-type-ai" active={type === "ai"} onClick={() => pick("ai")} icon={Sparkles} title="AI deep analysis" sub={aiPaid ? "Unlocked" : "From $3 to cover AI costs · $7 suggested"} />
        </div>
        {!(type === "ai" && aiPaid) && (
          <div className="mt-5">
            <p className="text-xs font-mono-ui text-slate-500">CHOOSE AN AMOUNT (USD)</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {PRESETS[type].map((p) => (
                <button key={p} data-testid={`amount-preset-${p}`} onClick={() => setAmount(p)}
                  className={`relative rounded-full px-4 py-2 text-sm border transition-colors ${Number(amount) === p ? "bg-[#1C242B] text-white border-[#1C242B]" : "bg-white border-[#1C242B]/15 hover:border-[#1C242B]/40"}`}>
                  ${p}{p === tier.suggested && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-[#D96B43]">suggested</span>}
                </button>
              ))}
              <input data-testid="amount-custom-input" type="number" min={type === "ai" ? 3 : 0} step="1" value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-24 rounded-full border border-[#1C242B]/15 px-4 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#D96B43]/40" />
            </div>
            {type === "ai" && <p className="text-xs text-slate-500 mt-3">The AI analysis uses a premium language model to write a personal narrative and 90-day plan. The minimum covers its running cost; anything more supports free access for others.</p>}
            {type === "static" && Number(amount) > 0 && Number(amount) < 1 && <p className="text-xs text-[#C05630] mt-2">Card payments need at least $1, or choose $0.</p>}
          </div>
        )}
        <button data-testid="modal-confirm-btn" onClick={go} disabled={busy}
          className="mt-6 w-full inline-flex justify-center items-center gap-2 rounded-full bg-[#D96B43] text-white px-6 py-3.5 font-semibold hover:bg-[#C05630] disabled:opacity-50 transition-colors">
          {busy ? <Loader2 size={18} className="animate-spin" /> : type === "static" && amount !== "" && Number(amount) === 0 ? <Download size={18} /> : <HeartHandshake size={18} />} {cta}
        </button>
        <p className="text-[11px] text-slate-500 text-center">Secure checkout by Stripe. Your contribution keeps this tool free for the next person.</p>
      </DialogContent>
    </Dialog>
  );
};
